import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getBiometricCredentials, isBiometricEnrolled as checkBiometricEnrolled, clearBiometricCredentials, storeBiometricCredentials, getAutoBiometricEnabled, setAutoBiometricEnabled as saveAutoBiometricEnabled } from '@/utils/secureStorage';
import { detectBiometricType, BiometricType } from '@/utils/biometricType';

// Define global callback functions for Despia Native Runtime
declare global {
  interface Window {
    onBioAuthSuccess: () => void;
    onBioAuthFailure: (errorCode: string, errorMessage: string) => void;
    onBioAuthUnavailable: () => void;
  }
}

export const useBiometricAuth = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [isBiometricEnrolled, setIsBiometricEnrolled] = useState(false);
  const [biometricType, setBiometricType] = useState<BiometricType>('unknown');
  const [autoBiometricEnabled, setAutoBiometricEnabledState] = useState(getAutoBiometricEnabled());
  const navigate = useNavigate();

  const setAutoBiometricEnabled = (enabled: boolean) => {
    saveAutoBiometricEnabled(enabled);
    setAutoBiometricEnabledState(enabled);
  };

  // Keep biometric credentials synced when Supabase rotates refresh tokens
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.refresh_token) {
        if (checkBiometricEnrolled()) {
          storeBiometricCredentials(session.refresh_token);
          console.log('Biometric credentials updated with new refresh token');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Check if running in Despia environment
    const isDespiaEnvironment = navigator.userAgent.includes("despia");
    setIsBiometricAvailable(isDespiaEnvironment);
    setIsBiometricEnrolled(checkBiometricEnrolled());
    
    // Detect biometric type (Face ID vs Touch ID)
    if (isDespiaEnvironment) {
      setBiometricType(detectBiometricType());
    }

    if (isDespiaEnvironment) {
      // Set up global callback functions for Despia Native Runtime
      window.onBioAuthSuccess = function() {
        console.log("Biometric authentication successful");
        setIsAuthenticating(false);
        
        // Handle successful biometric authentication
        const handleBioAuthSuccess = async () => {
          try {
            // Get the stored refresh token
            const refreshToken = getBiometricCredentials();
            
            if (!refreshToken) {
              toast.error("Please log in with email/password first to enable biometric auth");
              return;
            }

            // Use the refresh token to establish a session
            const { data, error } = await supabase.auth.refreshSession({
              refresh_token: refreshToken,
            });

            if (error) {
              console.error("Error refreshing session:", error);
              
              // If token was already used/rotated, clear stale credentials
              // AND purge the Supabase localStorage token so the next boot
              // doesn't retry with the same dead credential.
              if (error.message?.includes('refresh_token_already_used') || 
                  error.message?.includes('Invalid Refresh Token') ||
                  error.message?.includes('Refresh Token Not Found')) {
                clearBiometricCredentials();
                setIsBiometricEnrolled(false);
                try {
                  Object.keys(localStorage).forEach((key) => {
                    if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                      localStorage.removeItem(key);
                    }
                  });
                } catch {
                  // Best effort
                }
                toast.error("Biometric session expired. Please log in with your password to re-enable.");
              } else {
                toast.error("Biometric authentication failed. Please log in again.");
              }
              return;
            }

            if (data.session) {
              toast.success("Biometric authentication successful!");
              navigate("/");
            } else {
              toast.error("Authentication failed. Please log in again.");
            }
          } catch (error) {
            console.error("Error handling biometric auth success:", error);
            toast.error("Authentication error occurred");
          }
        };

        handleBioAuthSuccess();
      };

      window.onBioAuthFailure = function(errorCode: string, errorMessage: string) {
        console.log("Biometric authentication failed:", errorCode, errorMessage);
        setIsAuthenticating(false);
        toast.error(`Biometric authentication failed: ${errorMessage}`);
      };

      window.onBioAuthUnavailable = function() {
        console.log("Biometric authentication unavailable");
        setIsAuthenticating(false);
        setIsBiometricAvailable(false);
        
        // Offer to open iOS Settings
        toast.error("Biometric authentication is not set up on your device", {
          action: {
            label: "Open Settings",
            onClick: () => {
              import('despia-native').then(({ default: despia }) => {
                despia("settingsapp://");
              }).catch(console.error);
            },
          },
          duration: 6000,
        });
      };
    }

    return () => {
      // Clean up global functions
      if (isDespiaEnvironment) {
        delete window.onBioAuthSuccess;
        delete window.onBioAuthFailure;
        delete window.onBioAuthUnavailable;
      }
    };
  }, [navigate]);

  const triggerBiometricAuth = () => {
    if (!isBiometricAvailable) {
      toast.error("Biometric authentication is not available");
      return;
    }

    setIsAuthenticating(true);
    
    try {
      // Trigger biometric authentication via Despia SDK
      import('despia-native').then(({ default: despia }) => {
        despia('bioauth://');
        toast.info("Please authenticate using biometrics");
      }).catch((error) => {
        console.error("Error loading despia-native:", error);
        toast.error("Failed to trigger biometric authentication");
        setIsAuthenticating(false);
      });
    } catch (error) {
      console.error("Error triggering biometric auth:", error);
      toast.error("Failed to trigger biometric authentication");
      setIsAuthenticating(false);
    }
  };

  const disableBiometrics = () => {
    clearBiometricCredentials();
    setIsBiometricEnrolled(false);
    toast.success("Biometric authentication disabled");
  };

  return {
    isBiometricAvailable,
    isBiometricEnrolled,
    isAuthenticating,
    biometricType,
    autoBiometricEnabled,
    setAutoBiometricEnabled,
    triggerBiometricAuth,
    disableBiometrics,
  };
};
